import { useEffect, useState } from 'react';
import type { PlanDto } from '@sellokart/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AsyncSection } from '@/components/AsyncSection';
import { Button } from '@/components/Button';
import { IconCheck, IconClose, IconPencil } from '@/components/Icons';
import { PageHeader } from '@/components/PageHeader';
import { Skeleton } from '@/components/States';
import {
  fetchPlans,
  planKeys,
  updatePlan,
  usageKeys,
} from '@/features/plans/api';
import type { UpdatePlanInput } from '@/features/plans/api';
import { formatMoney } from '@/lib/format';
import { getErrorMessage } from '@/lib/api';

/**
 * The editable form state for one plan.
 *
 * Everything is a STRING, including the limits: an empty string is how "no
 * limit" is expressed in an <input type="number">, and holding these as numbers
 * would force a decision about what 0 and NaN mean on every keystroke. The
 * conversion to `number | null` happens once, on save.
 */
interface PlanDraft {
  maxBillsPerDay: string;
  maxBranches: string;
  maxDevices: string;
  pricePerMonth: string;
}

function toDraft(plan: PlanDto): PlanDraft {
  return {
    // null (no ceiling) renders as an empty field, never as "0".
    maxBillsPerDay: plan.maxBillsPerDay?.toString() ?? '',
    maxBranches: plan.maxBranches?.toString() ?? '',
    maxDevices: plan.maxDevices?.toString() ?? '',
    pricePerMonth: plan.pricePerMonth ?? '',
  };
}

/**
 * Blank means "no limit" — null, NOT zero.
 *
 * This is the single most important conversion on the page: writing 0 where the
 * operator meant "unlimited" would flag every tenant on that plan the moment
 * they traded at all.
 */
function toLimit(raw: string): number | null {
  const text = raw.trim();
  if (text === '') return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

/** Money stays a string all the way to the API — never parsed into a float. */
function toPrice(raw: string): string | null {
  const text = raw.trim();
  return text === '' ? null : text;
}

function draftToInput(draft: PlanDraft): UpdatePlanInput {
  return {
    maxBillsPerDay: toLimit(draft.maxBillsPerDay),
    maxBranches: toLimit(draft.maxBranches),
    maxDevices: toLimit(draft.maxDevices),
    pricePerMonth: toPrice(draft.pricePerMonth),
  };
}

/**
 * `unit` is the SINGULAR noun; the plural is added only when the count calls
 * for it. Starter allows exactly one branch, so a naive `${value} branches`
 * renders "1 branches" on the very first card an operator sees.
 */
function formatLimit(value: number | null, unit: string, plural = `${unit}s`): string {
  if (value === null) return 'No limit';
  return `${value.toLocaleString()} ${value === 1 ? unit : plural}`;
}

function LimitField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-slate-500">
        {label}
      </span>
      <input
        type="number"
        min={1}
        inputMode="numeric"
        className="input"
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

/**
 * One plan, shown as a read-only summary by default — a pricing-page card, not
 * a spreadsheet row — with an edit affordance that swaps the whole card into a
 * form. Only one field set is live at a time, and it is the one the operator
 * explicitly asked to change: editing every plan's limits at once invites a
 * mis-click on a field nobody was looking at.
 */
function PlanCard({ plan }: { plan: PlanDto }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<PlanDraft>(() => toDraft(plan));
  const [saved, setSaved] = useState(false);

  // A refetch (or another tab's edit) must not be overwritten by stale local
  // state, so the draft re-seeds whenever the server's copy changes.
  useEffect(() => {
    setDraft(toDraft(plan));
  }, [plan]);

  const mutation = useMutation({
    mutationFn: () => updatePlan(plan.id, draftToInput(draft)),
    onSuccess: () => {
      // Editing a limit re-scores every tenant on this plan, so the usage views
      // are stale the moment this succeeds.
      void queryClient.invalidateQueries({ queryKey: planKeys.all });
      void queryClient.invalidateQueries({ queryKey: usageKeys.all });
      setEditing(false);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    },
  });

  const set = (key: keyof PlanDraft) => (value: string) =>
    setDraft((current) => ({ ...current, [key]: value }));

  const startEditing = () => {
    setDraft(toDraft(plan));
    mutation.reset();
    setEditing(true);
  };

  const cancelEditing = () => {
    setDraft(toDraft(plan));
    mutation.reset();
    setEditing(false);
  };

  return (
    <div className="card flex h-full flex-col p-5">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-base font-semibold text-slate-900">{plan.name}</h3>
        {!editing ? (
          <Button
            size="sm"
            variant="ghost"
            icon={<IconPencil className="h-3.5 w-3.5" />}
            onClick={startEditing}
            aria-label={`Edit ${plan.name}`}
          >
            Edit
          </Button>
        ) : saved ? (
          <span className="text-xs font-medium text-emerald-700">Saved</span>
        ) : null}
      </div>

      <p className="mt-1 text-2xl font-semibold text-slate-900">
        {formatMoney(plan.pricePerMonth, plan.currency, 'Negotiated')}
        {plan.pricePerMonth ? (
          <span className="text-sm font-normal text-slate-500"> / month</span>
        ) : null}
      </p>

      {editing ? (
        <div className="mt-5 flex flex-1 flex-col space-y-3 border-t border-slate-100 pt-5">
          {mutation.isError ? (
            <p className="text-xs text-rose-700">
              {getErrorMessage(mutation.error, 'Could not save this plan.')}
            </p>
          ) : null}
          <LimitField
            label="Bills / day"
            value={draft.maxBillsPerDay}
            onChange={set('maxBillsPerDay')}
            placeholder="No limit"
          />
          <LimitField
            label="Branches"
            value={draft.maxBranches}
            onChange={set('maxBranches')}
            placeholder="No limit"
          />
          <LimitField
            label="Tills"
            value={draft.maxDevices}
            onChange={set('maxDevices')}
            placeholder="No limit"
          />
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-500">
              Price / month
            </span>
            <input
              type="text"
              inputMode="decimal"
              className="input"
              value={draft.pricePerMonth}
              placeholder="Negotiated"
              onChange={(event) => set('pricePerMonth')(event.target.value)}
            />
          </label>
          <div className="flex flex-1 items-end justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              icon={<IconClose className="h-3.5 w-3.5" />}
              onClick={cancelEditing}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              icon={<IconCheck className="h-3.5 w-3.5" />}
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      ) : (
        // A plain bulleted list, top to bottom, like a pricing-page feature
        // list — not a 3-column stat grid, which reads fine full-width but
        // cramped in a card this narrow.
        <ul className="mt-5 flex-1 space-y-2.5 border-t border-slate-100 pt-5 text-sm text-slate-700">
          <li className="flex items-baseline gap-1.5">
            <span aria-hidden="true" className="text-slate-300">
              •
            </span>
            {formatLimit(plan.maxBillsPerDay, 'bill / day', 'bills / day')}
          </li>
          <li className="flex items-baseline gap-1.5">
            <span aria-hidden="true" className="text-slate-300">
              •
            </span>
            {formatLimit(plan.maxBranches, 'branch', 'branches')}
          </li>
          <li className="flex items-baseline gap-1.5">
            <span aria-hidden="true" className="text-slate-300">
              •
            </span>
            {formatLimit(plan.maxDevices, 'till')}
          </li>
        </ul>
      )}
    </div>
  );
}

/**
 * Edit the subscription tiers. No create or delete by design: a fifth plan is a
 * pricing decision deserving a migration, not a button whose mis-click strands
 * tenants on a deleted plan.
 */
export function PlansPage() {
  const query = useQuery({
    queryKey: planKeys.list(),
    queryFn: ({ signal }) => fetchPlans(signal),
  });

  return (
    <div>
      <PageHeader
        title="Plans"
        description="Limits warn, they never block. A tenant over their plan is flagged on the tenants list; their tills keep trading."
      />

      <AsyncSection
        state={query}
        skeleton={
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="card p-5">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="mt-2 h-7 w-24" />
                <div className="mt-5 space-y-2.5 border-t border-slate-100 pt-5">
                  {Array.from({ length: 3 }).map((__, row) => (
                    <Skeleton key={row} className="h-3.5 w-24" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        }
        errorTitle="Could not load plans"
      >
        {(plans) => (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {plans.map((plan) => (
              <PlanCard key={plan.id} plan={plan} />
            ))}
          </div>
        )}
      </AsyncSection>
    </div>
  );
}
